/** @format */

import { useEffect, useState } from "react";
import { Brand, Paper } from "@question-bank/shared";
import { listPapers } from "../api/papers";

/**
 * Session-level cache of the papers request per brand. Flag indicators and the
 * export bundler both need paper data; caching the promise means many question
 * cards share a single request rather than each firing their own.
 */
const papersPromiseCache = new Map<Brand, Promise<Paper[]>>();

/** Load (and cache for the session) every paper for a brand. */
export const loadPapers = (brand: Brand): Promise<Paper[]> => {
	let cached = papersPromiseCache.get(brand);
	if (!cached) {
		cached = listPapers(brand).catch((error) => {
			// Don't poison the cache on failure — allow a later retry.
			papersPromiseCache.delete(brand);
			throw error;
		});
		papersPromiseCache.set(brand, cached);
	}
	return cached;
};

/**
 * Returns the set of paper ids (for the brand) that carry at least one
 * attachment, so callers can flag questions with additional resources.
 */
export const usePapersWithAttachments = (brand: Brand): Set<string> => {
	const [paperIds, setPaperIds] = useState<Set<string>>(new Set());

	useEffect(() => {
		let isCancelled = false;
		loadPapers(brand)
			.then((papers) => {
				if (isCancelled) {
					return;
				}
				setPaperIds(new Set(papers.filter((paper) => (paper.attachments?.length ?? 0) > 0).map((paper) => paper.id)));
			})
			.catch(() => {
				// Flagging is best-effort; leave the set empty on failure.
			});
		return () => {
			isCancelled = true;
		};
	}, [brand]);

	return paperIds;
};

/**
 * Load a single paper (with its attachments) by its deterministic id, reusing
 * the session paper cache. Returns `null` until loaded or when not found.
 */
export const usePaperById = (brand: Brand, paperId: string | undefined): Paper | null => {
	const [paper, setPaper] = useState<Paper | null>(null);

	useEffect(() => {
		if (!paperId) {
			setPaper(null);
			return;
		}
		let isCancelled = false;
		loadPapers(brand)
			.then((papers) => {
				if (!isCancelled) {
					setPaper(papers.find((candidate) => candidate.id === paperId) ?? null);
				}
			})
			.catch(() => {
				// Best-effort; leave as null on failure.
			});
		return () => {
			isCancelled = true;
		};
	}, [brand, paperId]);

	return paper;
};
